import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "../../../accordion/Accordion";
import { defaultPersonKursistColumns } from "../../../../config/person";
import { extractCurrentPersonFirma } from "../../../../util/person";
import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../api/person/queries";
import PersonKursist from "./Kursus/PersonKursist";
import RetssagsProever from "./Kursus/RetssagsProever";
import useActiveColumns from "../../../../hooks/useActiveColumns";
import { KursusPerson, PersonDto } from "../../../../interfaces/schema";
import usePersonChecks from "../../../../hooks/errorHandling/usePersonChecks";
import { useState } from "react";
import { transformQueryDataPerioder } from "../../../../util/apollo";

export default function Kursus({ personId }: { personId: string }) {
  const [fmuKurserColumns, setFmuKurserActiveColumns] = useActiveColumns(
    "person-personfmukurser-active-columns",
    defaultPersonKursistColumns,
  );
  const [andreArrangementerColumns, setAndreArrangementerActiveColumns] =
    useActiveColumns(
      "person-personandrearrangementer-active-columns",
      defaultPersonKursistColumns
        .map((c) => {
          if (c.field === "hold.kursustypeNavigation.navn") {
            return { ...c, active: true };
          }
          return c;
        })
        .filter((pk) => pk.field !== "bestaaetMark")
        .filter((pk) => pk.field !== "tilmeldtRaekkefoelge"),
    );

  const [data, setData] = useState<
    | {
        person: PersonDto;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch, networkStatus } = useQuery<{
    person: PersonDto;
  }>(
    PERSON_BY_ID_QUERY("personKursusByIdQuery", {
      kursusPerson: true,
      fmuretssagsproeveEksaminand: true,
    }),
    {
      variables: { id: personId },
      notifyOnNetworkStatusChange: true,
      onCompleted: (d) => {
        const transformed = transformQueryDataPerioder(d);
        setData(transformed);
      },
    },
  );
  const { checkResult, person } = usePersonChecks(
    loading,
    error,
    data,
    networkStatus,
  );
  if (checkResult) return checkResult;
  const currentFirma = extractCurrentPersonFirma(person);

  const { kursusPerson, fmuretssagsproeveEksaminand } = person;
  const { fmuKurser, andreArrangementer } =
    kursusPerson?.reduce<KursusGrouping>(
      (acc, pk) => {
        const nr = pk.hold.kursustypeNavigation?.nr;
        (nr === 3 || nr === 5 ? acc.fmuKurser : acc.andreArrangementer).push(
          pk,
        );
        return acc;
      },
      { fmuKurser: [], andreArrangementer: [] },
    ) ?? { fmuKurser: [], andreArrangementer: [] };
  const currentAdresse = person.adresse?.adresse;

  return (
    <div className="container">
      <div className="c-card">
        <div className="row gx-6">
          <div className="col-12 col-lg-4 mt-3">
            <div className="c-item-list">
              <div className="flex-100">
                <h5 className="form-label">Adresse</h5>
                <div
                  className={
                    currentFirma
                      ? "c-item-list"
                      : "d-flex align-items-center justify-content-center"
                  }
                >
                  {currentFirma ? (
                    <div
                      className="form-control--value"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {currentFirma.firma.navn1} <br />
                      {currentFirma.firma.beliggenhedsadresse.toLines.join(
                        "\n",
                      )}
                    </div>
                  ) : currentAdresse ? (
                    <div
                      className="form-control--value"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {currentAdresse.toLines.join("\n")}
                    </div>
                  ) : (
                    <p className="text-muted">-</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-8 mt-3">
            <div className="c-item-list">
              <div>
                <h5 className="form-label">E-mail, arbejde</h5>
                <div className="form-control--value form-control--value">
                  {currentFirma && currentFirma.emailArbejde ? (
                    currentFirma.emailArbejde
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              </div>
              <div>
                <h5 className="form-label">E-mail, privat</h5>
                <div className="form-control--value form-control--value">
                  {!person.emailPrivat && <span className="text-muted">-</span>}
                  {person.emailPrivat && person.emailPrivat.length > 0 && (
                    <>{person.emailPrivat}</>
                  )}
                </div>
              </div>
              <div>
                <h5 className="form-label">Telefon, arbejde</h5>
                <div className="form-control--value">
                  {currentFirma && currentFirma.firma.tlf ? (
                    currentFirma.firma.tlf
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              </div>
              <div>
                <h5 className="form-label">Mobiltelefon</h5>
                <div className="form-control--value">
                  {!person.tlfMobilNr && <span className="text-muted">-</span>}
                  {person.tlfMobilNr && person.tlfMobilNr.length > 0 && (
                    <>{person.tlfMobilNr}</>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AccordionRoot localstorageKey="person-id-kursus-accordion-value">
        <AccordionItem
          className="c-card c-card--accordion"
          value="fmukurser"
          isEmpty={!fmuKurser?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>FMU-kurser</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <PersonKursist
              localStorageSortKey="personFmuKurserSortBy"
              personKursist={fmuKurser ?? []}
              personKursistColumns={fmuKurserColumns}
              setPersonKursistActiveColumns={setFmuKurserActiveColumns}
              invalidateData={refetch}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="retssagsproever"
          isEmpty={!fmuretssagsproeveEksaminand?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Retssagsprøver</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <RetssagsProever person={person} invalidateData={refetch} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="andre"
          isEmpty={!andreArrangementer?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Andre arrangementer</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <PersonKursist
              localStorageSortKey="personAndreArrangementerSortBy"
              personKursist={andreArrangementer ?? []}
              personKursistColumns={andreArrangementerColumns}
              setPersonKursistActiveColumns={setAndreArrangementerActiveColumns}
              invalidateData={refetch}
            />
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
}
interface KursusGrouping {
  fmuKurser: KursusPerson[];
  andreArrangementer: KursusPerson[];
}
